import { forwardRef, IChildren } from "@zap/utils/lib/ReactHelpers";
import * as React from "react";
import { HTMLProps, PropsWithoutRef, ReactNode, Ref } from "react";
import { halfBleed, noSpacing, Row } from "./Box";
import { buttonIcon, buttonStyle, buttonWrapper, containedButton, disabledButton, flatButton, loadingButton, outlinedButton } from "./ButtonStyles";
import { Clickable, ClickEvent } from "./Clickable";
import { duration } from "./CommonStyles";
import { useFormButtonClick, useFormLabelSpacing } from "./Form";
import { Icon, IconName, IconSizes } from "./Icons/Icon";
import { Loading } from "./Loading";
import { standardSpacing } from "./Sizes";
import { style, StyleCollection, Styled, transition } from "./styling";

type HTMLButtonProps = PropsWithoutRef<HTMLProps<HTMLButtonElement>>

export interface IButtonProps {
    icon?: IconName;
    onClick?: (e: ClickEvent) => void;
    hideRipple?: boolean;
    outlined?: boolean;
    contained?: boolean;
    disabled?: boolean;
    selected?: boolean;
    dangerous?: boolean;
    grey?: boolean;
    white?: boolean;
    styles?: StyleCollection;
    isLoading?: boolean;
    children?: ReactNode;
    minWidth?: number;
}

export const Button = forwardRef(function Button(props: IButtonProps & HTMLButtonProps, ref: Ref<HTMLButtonElement>) {
    let { dangerous, grey, white, styles, onClick, isLoading, hideRipple, disabled, outlined, contained, icon, type, ...buttonProps } = props;
    let isDisabled = disabled || isLoading;

    let buttonClass = contained || isLoading ? containedButton
        : outlined ? outlinedButton
            : flatButton;

    let formClick = useFormButtonClick(type, onClick);

    return <Clickable
        color={dangerous ? 'dangerous' : grey ? 'darkGrey' : white ? 'white' : 'default'}
        invert={contained}
        hideRipple={hideRipple || isLoading}
        disabled={isDisabled}
        onClick={isLoading ? undefined : formClick}
        styles={[buttonWrapper, buttonClass, isDisabled && disabledButton, !(contained || outlined) && halfBleed]}
        ignoreKeyboardEvents>
        {({ getFocusProps }) =>
            <Styled.button
                ref={ref}
                type="button"
                role="button"
                styles={[buttonStyle, textButton, isLoading && loadingButton, styles]} /* disable with styling so mouse events are still triggered, Clickable prevents onClick */
                inline={{ minWidth: props.minWidth }}
                tabIndex={isDisabled ? -1 : undefined} /* prevent keyboard navigation if disabled */
                aria-disabled={isDisabled}
                {...getFocusProps(buttonProps)}>
                <Row halfSpacing justifyContent="center" alignItems="center" width="100%">
                    {icon && <Icon name={icon} size="tiny" styles={buttonIcon} color="currentColor" />}
                    {props.children && <div>
                        {props.children}
                    </div>}
                    <Row styles={[spinner, isLoading ? loadingSpinner : noSpacing]}>
                        {isLoading && <Loading size="tiny" />}
                    </Row>
                </Row>
            </Styled.button>
        }
    </Clickable>;
});

let textButton = style('button-textButton', {
    minWidth: 64
});

export let spinner = style('button-spinner', {
    marginTop: -1,
    marginLeft: 0,
    width: 0,
    transform: { scale: 0 },
    transition: transition('all', duration.medium)
});

let loadingSpinner = style('button-spinner-loading', {
    width: IconSizes.tiny,
    transform: { scale: 1 }
});

export interface IButtonListProps extends IChildren {
    align?: 'start' | 'end';
}

export const ButtonList = forwardRef(function ButtonList(props: IButtonListProps, ref: Ref<any>) {
    let formSpacing = useFormLabelSpacing();

    return <Row
        ref={ref}
        justifyContent={props.align === 'start' ? 'flex-start' : 'flex-end'}
        noSpacing
        sidePadding
        styles={[buttonListSpacing, formSpacing]}>
        {props.children}
    </Row>
});

let buttonListSpacing = style('buttonList-spacing', {
    $: {
        [`> .${buttonWrapper} + .${buttonWrapper}`]: {
            marginLeft: standardSpacing
        },
        [`> * + .${flatButton}.${buttonWrapper}`]: {
            marginLeft: standardSpacing / 2
        },
        [`> .${flatButton}.${buttonWrapper} + *`]: {
            marginLeft: standardSpacing / 2
        }
    }
});
